import {
  Alert,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  AlertTriangle,
  ArrowRight,
  Eye,
  EyeOff,
  Users,
  BookOpen,
  Edit3,
  Zap,
  Layers,
  Briefcase,
  Clock,
} from 'react-feather';
import { CircleBackButton } from '../../components/common/CircleBackButton';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFeatureFlag } from '../../stores/featureFlags';
import { InSessionHeaderBar } from '../../components/layout/InSessionHeaderBar';
import {
  coachActiveSessionApi,
  type ActiveSessionState,
} from '../../services/api/coachActiveSession';
import {
  ActiveSessionPreviewHeaderBar,
  type PreviewViewMode,
} from './components/active-session/ActiveSessionPreviewHeaderBar';
import { ActiveSessionGuidePanel } from './components/active-session/ActiveSessionGuidePanel';
import { ActiveSessionWhiteboardPanel } from './components/active-session/ActiveSessionWhiteboardPanel';
import { ActiveSessionNotesPanel } from './components/active-session/ActiveSessionNotesPanel';
import { ActiveSessionParticipantsPanel } from './components/active-session/ActiveSessionParticipantsPanel';
import { ActiveSessionAiInsightsPanel } from './components/active-session/ActiveSessionAiInsightsPanel';
import { ActiveSessionSlideCard } from '../participant/sessionActive/components/ActiveSessionSlideCard';
import { ParticipantSlideCounter } from '../participant/sessionActive/components/ParticipantSlideCounter';
import type {
  AuthoredAgendaSectionForParticipant,
  ParticipantActiveSessionSlideDeck,
} from '../../services/api/participantActiveSession';

/**
 * MED-873 / Story 2.3c — Coach Active Session Preview page.
 *
 * Route: /coach/cohorts/:cohortId/sessions/:sessionId/preview
 *
 * Read-only render of the MED-792 Active Session screen, accessible from the
 * Preview button in the Session Prep flow. Composes the same four panels but:
 *   - Uses `GET /api/coach/sessions/:sessionId/preview-state` (no side effects).
 *   - Manages section index + SHOW bar toggles in local state — no API mutation.
 *   - Renders a persistent `ActiveSessionPreviewHeaderBar` above the layout.
 *   - All four panels receive `isReadOnly` (where supported); the Notes panel's
 *     TipTap editor is disabled.
 *   - Timers are static (`00:00 Total` + step budget) — no live ticker.
 *   - AC5 stub: when the AI Insights SHOW toggle is on, the empty
 *     `ActiveSessionAiInsightsPanel` renders in place of the real (MED-872) panel.
 *
 * IV1 contract: this page calls no mutation endpoints. Session status, timers,
 * and notifications are unaffected by entering and leaving preview mode.
 */

const SHOW_DEFAULTS = {
  guide: true,
  notes: true,
  participants: false,
  aiInsights: false,
} as const;

// Floor for the measured 3-panel grid height so the preview frame never
// collapses below a usable size on short viewports (columns scroll inside).
const MIN_PANEL_GRID_HEIGHT = 420;

// MED-1068 — token values mirrored from the live MED-1011 in-session header
// (CoachActiveSessionPage) so the preview chrome matches it without coupling to
// that page. Keep in sync with the live constants if they change.
const BAND_BG = '#FFFFFF'; // header band background
const BAND_BORDER = '#E5E7EB'; // band dividers / inactive pill + button border
const TAB_ACTIVE = '#1B2B3A'; // active step-tab underline + label (navy)
const TAB_INACTIVE = '#9CA3AF'; // inactive tab / SHOW pill text
const TIMER_OVER = '#D97706'; // step timer text when over budget
const NAV_NAVY = '#203358'; // section-nav primary (Next)
const SHOW_ON = '#2D7B77'; // SHOW pill border + text when active
const SHOW_ON_BG = '#F0FAF9'; // SHOW pill background when active
const PAGE_BG = '#F2F0ED'; // in-session page surface (matches InSessionLayout)
const NAV_BTN_WIDTH = 140; // fixed nav button width (live parity)

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Static-timer formatter — preview never ticks, so this just renders 0s or a budget. */
function formatStaticElapsed(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  if (h > 0) return `${h}:${pad2(m)}:${pad2(s)}`;
  return `${pad2(m)}:${pad2(s)}`;
}

interface ShowToggleProps {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  badge?: string | number;
  onClick: () => void;
  'data-testid'?: string;
}

// MED-1068 AC5 — outlined green-pill SHOW toggle matching the live MED-1011
// header (replacing the old filled solid-green button). Functional in preview's
// local-only state; no mutations.
function ShowToggle({
  active,
  label,
  icon,
  badge,
  onClick,
  ...rest
}: ShowToggleProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      data-testid={rest['data-testid']}
      data-active={active ? 'true' : 'false'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 28,
        padding: '6px 11px',
        borderRadius: 6,
        border: `1px solid ${active ? SHOW_ON : BAND_BORDER}`,
        backgroundColor: active ? SHOW_ON_BG : '#FFFFFF',
        color: active ? SHOW_ON : TAB_INACTIVE,
        fontWeight: 700,
        fontSize: 11,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ display: 'inline-flex' }} aria-hidden="true">
        {icon}
      </span>
      {label}
      {badge !== undefined && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 16,
            height: 16,
            padding: '0 5px',
            borderRadius: 8,
            fontSize: 9,
            fontWeight: 700,
            backgroundColor: '#3B82B0',
            color: '#FFFFFF',
          }}
        >
          {badge}
        </span>
      )}
      {active ? (
        <Eye size={12} aria-hidden="true" />
      ) : (
        <EyeOff size={12} aria-hidden="true" />
      )}
    </UnstyledButton>
  );
}

export interface CoachActiveSessionPreviewPageProps {
  /**
   * MED-1068 — DEV-only static preview. Seeds the preview state and bypasses
   * the feature-flag gate + the read-only fetch so the polished preview chrome
   * renders from an in-memory fixture (`/dev/session-preview`). `contextOverride`
   * selects the coach vs. peer-leader (participant) variant independently of the
   * URL; `cameFromSessionContent` mimics the CM entry. Compiled out of
   * production builds (gated at the route).
   */
  preview?: {
    state: ActiveSessionState;
    contextOverride?: 'coach' | 'participant';
    cameFromSessionContent?: boolean;
  };
}

export function CoachActiveSessionPreviewPage({
  preview,
}: CoachActiveSessionPreviewPageProps = {}) {
  const isPreview = !!preview;
  const params = useParams<{ cohortId: string; sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Fall back to the seeded fixture's ids in the DEV preview (the /dev route
  // carries no :cohortId/:sessionId params).
  const cohortId = params.cohortId ?? preview?.state.cohort_id;
  const sessionId = params.sessionId ?? preview?.state.session_id;
  // MED-814 — the peer-leader prep flow also opens this preview via
  // `/participant/cohorts/:cohortId/sessions/:sessionId/preview`. Detect via the
  // current pathname so the close-preview action returns to the participant prep
  // page instead of the coach prep page. The DEV preview passes an explicit
  // override since its path carries no `/participant/` prefix.
  const isParticipantContext = preview?.contextOverride
    ? preview.contextOverride === 'participant'
    : location.pathname.startsWith('/participant/');
  // MED-965: When the CM enters from the Session Content page, the entry adds
  // `?from=session-content` so Close Preview returns there instead of the coach
  // prep page (which CMs don't see). The data side of CM context (blank coach
  // notes / participants) is handled at the API layer; this branch is UX only.
  const cameFromSessionContent =
    preview?.cameFromSessionContent ??
    searchParams.get('from') === 'session-content';
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  const [state, setState] = useState<ActiveSessionState | null>(
    preview?.state ?? null
  );
  const [loading, setLoading] = useState(!preview);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // "Leader View" (leader panels) vs "Participant View" (read-only participant
  // slide experience). Toggled from the preview header bar. The internal value
  // stays 'coach'; only the label is "Leader View" (MED-1218 AC6).
  const [viewMode, setViewMode] = useState<PreviewViewMode>('coach');

  // Local view state — section navigation and SHOW bar (AC3, AC4).
  // Preview does NOT call any mutation endpoint when these change.
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
  // MED-976/E3.3a — locally-selected "main" slide (preview is read-only, so no
  // mutation; mirrors the live page's coach-driven selection). Reset to the
  // section's first slide whenever the section changes (see effect below).
  const [previewSlidePage, setPreviewSlidePage] = useState<number | null>(null);
  useEffect(() => {
    setPreviewSlidePage(null);
  }, [currentSectionIndex]);
  const [showGuide, setShowGuide] = useState<boolean>(SHOW_DEFAULTS.guide);
  const [showNotes, setShowNotes] = useState<boolean>(SHOW_DEFAULTS.notes);
  const [showParticipants, setShowParticipants] = useState<boolean>(SHOW_DEFAULTS.participants);
  const [showAiInsights, setShowAiInsights] = useState<boolean>(SHOW_DEFAULTS.aiInsights);

  // Mirror of the live page fix: pin the 3-panel grid to the remaining viewport
  // height so a long Session Guide scrolls inside its own column instead of
  // stretching the row and dragging the whiteboard tall.
  const panelGridRef = useRef<HTMLDivElement>(null);
  const [panelGridHeight, setPanelGridHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = panelGridRef.current;
    if (!el) return;
    const recompute = () => {
      const top = el.getBoundingClientRect().top;
      const next = window.innerHeight - top - 24;
      setPanelGridHeight(Math.max(next, MIN_PANEL_GRID_HEIGHT));
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [loading]);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    try {
      const fresh = await coachActiveSessionApi.getPreviewState(sessionId);
      setState(fresh);
      setErrorMsg(null);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isPreview) return;
    refresh();
  }, [refresh, isPreview]);

  const currentSection = useMemo(() => {
    if (!state) return null;
    return state.sections[currentSectionIndex] ?? null;
  }, [state, currentSectionIndex]);

  // Adapt the coach preview-state into the slim participant shapes so the
  // "Participant View" toggle renders exactly what participants see. Leader
  // prompts are intentionally dropped (participants never receive them — the
  // participant section type has no `leader_prompts` field).
  const participantSections = useMemo<AuthoredAgendaSectionForParticipant[]>(() => {
    if (!state) return [];
    return state.sections.map(s => ({
      id: s.id,
      section_name: s.section_name,
      duration_minutes: s.duration_minutes,
      sort_order: s.sort_order,
      participant_prompt: s.participant_prompt,
      show_commitment_field: s.show_commitment_field,
      show_last_session_commitment: s.show_last_session_commitment,
      visuals: s.visuals,
    }));
  }, [state]);

  const participantSlideDeck = useMemo<ParticipantActiveSessionSlideDeck | null>(() => {
    if (!state?.slide_deck) return null;
    const { id, converted_pdf_s3_key, processing_state, original_filename } = state.slide_deck;
    return { id, converted_pdf_s3_key, processing_state, original_filename };
  }, [state]);

  const handleClose = () => {
    if (cohortId && sessionId) {
      const returnPath = cameFromSessionContent
        ? `/cohorts/${cohortId}/sessions/${sessionId}/content`
        : isParticipantContext
          ? `/participant/sessions/${sessionId}/prep`
          : `/coach/cohorts/${cohortId}/sessions/${sessionId}/prep`;
      navigate(returnPath);
    } else if (isParticipantContext) {
      navigate('/participant');
    } else {
      navigate(cameFromSessionContent ? '/cohorts' : '/coach');
    }
  };

  // ----- Render gates -----
  if (!cohortId || !sessionId) {
    return <Navigate to={isParticipantContext ? '/participant' : '/coach'} replace />;
  }

  if (!isPreview && !liveSessionDeliveryEnabled) {
    return (
      <Container size="md" py="xl">
        <Alert
          color="warningAmber"
          icon={<AlertTriangle size={16} />}
          title="This feature isn't available yet"
        >
          The live session delivery experience is gated by a feature flag that is currently off
          in this environment.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Group justify="center">
          <Loader />
          <Text>Loading preview…</Text>
        </Group>
      </Container>
    );
  }

  if (errorMsg || !state) {
    // MED-1231 (AC6) — in the CM content-preview context, never surface the raw
    // backend string. The live Active Session guards throw coach-worded copy
    // ("active session screen is only accessible for coach-led facilitation")
    // that makes no sense while authoring content. Replace it with a message
    // written for the authoring context. (The peer-led block itself is removed
    // at the API for CM preview — MED-1231 backend — so this now only covers
    // genuine load failures.) Outside the content context, keep surfacing the
    // original message so coach / peer-leader previews are unaffected.
    const previewErrorMessage = cameFromSessionContent
      ? "We couldn't load a preview of this session's content. Save your content and try again — if the problem continues, make sure the session's agenda has been set up."
      : (errorMsg ?? 'Preview state is unavailable.');
    return (
      <Container size="md" py="xl">
        <Stack gap="md">
          <Alert color="dangerRed" icon={<AlertTriangle size={16} />} title="Couldn't load preview">
            {previewErrorMessage}
          </Alert>
          <Group>
            <CircleBackButton onClick={() => navigate(-1)} />
          </Group>
        </Stack>
      </Container>
    );
  }

  // ----- Render -----
  const sectionCount = state.sections.length;
  const canRetreat = currentSectionIndex > 0;
  const canAdvance = currentSectionIndex < sectionCount - 1;
  const stepAllottedMin = currentSection?.duration_minutes ?? null;
  // Preview is read-only and never ticks (AC3), so the timers are pinned at
  // zero; the over-budget branch can't trigger but the coloring logic mirrors
  // the live header.
  const totalElapsedSec = 0;
  const stepElapsedSec = 0;
  const isStepOverBudget =
    stepAllottedMin !== null && stepElapsedSec > stepAllottedMin * 60;
  // MED-1218 — the AI Insights toggle is surfaced whenever AI is enabled for
  // the session (modality AI flag + the group's AI Listening toggle), for every
  // viewer previewing the leader's content: coach, peer leader, and CM. The
  // former MED-816 AC8 peer-leader suppression (`&& !isParticipantContext`) is
  // removed here to bring preview to parity with live delivery, which dropped
  // the same clause per MED-909 / E4.6 (see CoachActiveSessionPage `aiActive`).
  // Removing the clause cannot expose AI Insights to real participants: they
  // render a different component, so inside this leader preview page
  // `isParticipantContext` is only ever true for a peer leader.
  const showAiInsightsToggle =
    state.ai_features_enabled && state.ai_listening_enabled;

  return (
    <Box
      data-testid="active-session-preview-page"
      style={{ minHeight: '100vh', backgroundColor: PAGE_BG }}
    >
      {/* MED-1068 AC1 — the black Session Preview bar is the topmost chrome,
          with the reduced in-session header (logo + user, no global nav) beneath
          it. This page renders outside CoachLayout/ParticipantLayout (see
          App.tsx) so the full app nav is no longer the outer frame. */}
      <ActiveSessionPreviewHeaderBar
        sessionTitle={state.title}
        sessionNumber={state.session_number}
        sectionIndex={currentSectionIndex}
        sectionTitle={currentSection?.section_name ?? null}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onClose={handleClose}
      />
      <InSessionHeaderBar
        contextOverride={isParticipantContext ? 'participant' : 'coach'}
      />

      {/* MED-1068 — header rebuilt to match the live MED-1011 bands: a §A
          session-header band, a §B agenda strip (navy underline tabs), and a §D
          SHOW band — each a full-width white band beneath the preview chrome. */}
      {/* §A SESSION HEADER band */}
      <Box
        style={{
          backgroundColor: BAND_BG,
          borderBottom: `1px solid ${BAND_BORDER}`,
          padding: '20px 24px 15px',
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Stack gap={6} style={{ flexGrow: 1, minWidth: 0 }}>
            <Group gap={16} align="flex-end" wrap="nowrap">
              <Text fz={24} fw={700} c="#1a1a1a" lh={1.1}>
                Session {state.session_number ?? ''}: {state.title}
              </Text>
              {state.group_name && (
                <Text fz={16} fw={600} c="#1a1a2e" lh={1.2}>
                  {state.group_name}
                </Text>
              )}
            </Group>
            <Group gap={14} align="center">
              <Group gap={6} align="center" wrap="nowrap">
                <Layers size={14} color="#888" aria-hidden="true" />
                <Text fz={13} c="#888">
                  {state.cohort_name}
                </Text>
              </Group>
              {state.organization_name && (
                <Group gap={6} align="center" wrap="nowrap">
                  <Briefcase size={14} color="#888" aria-hidden="true" />
                  <Text fz={13} c="#888">
                    {state.organization_name}
                  </Text>
                </Group>
              )}
              {state.duration_minutes !== null && (
                <Group gap={6} align="center" wrap="nowrap">
                  <Clock size={14} color="#888" aria-hidden="true" />
                  <Text fz={13} c="#888">
                    {state.duration_minutes} minutes
                  </Text>
                </Group>
              )}
            </Group>
          </Stack>

            {/* MED-1068 AC3 — polished MED-1011 timer treatment: a status dot +
                inline label with green→amber over-budget coloring. Static in
                preview (read-only — no live tick), so the step is never over
                budget and stays green; the coloring logic mirrors live. */}
            <Group
              gap={12}
              align="center"
              wrap="nowrap"
              data-testid="active-session-preview-timers"
            >
              <Box
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 16,
                  height: 16,
                  borderRadius: 8,
                  border: '2px solid rgba(76,175,80,0.4)',
                }}
              >
                <Box
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#4CAF50',
                  }}
                />
              </Box>

              <Group gap={6} align="center" wrap="nowrap">
                <Text
                  fw={700}
                  fz={20}
                  lh={1}
                  c="medleyGreen"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatStaticElapsed(totalElapsedSec)}
                </Text>
                <Text fz={16} lh={1} c="#666">
                  Total
                </Text>
              </Group>

              <Box
                style={{
                  width: 1,
                  height: 20,
                  backgroundColor: '#9d9d9d',
                  flexShrink: 0,
                }}
              />

              <Group gap={6} align="center" wrap="nowrap">
                <Text
                  fw={700}
                  fz={20}
                  lh={1}
                  c={isStepOverBudget ? undefined : 'medleyGreen'}
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    ...(isStepOverBudget ? { color: TIMER_OVER } : {}),
                  }}
                  data-testid="active-session-preview-step-timer"
                >
                  {formatStaticElapsed(stepElapsedSec)}
                </Text>
                <Text
                  fz={16}
                  lh={1}
                  c={isStepOverBudget ? undefined : '#666'}
                  style={isStepOverBudget ? { color: TIMER_OVER } : undefined}
                >
                  Step {currentSectionIndex + 1}
                </Text>
              </Group>
            </Group>
          </Group>
        </Box>

        {/* §B AGENDA STRIP band — navy 3px-underline tabs + section nav. */}
        <Box
          style={{
            backgroundColor: BAND_BG,
            borderBottom: `1px solid ${BAND_BORDER}`,
            padding: '0 24px',
          }}
        >
          <Group justify="space-between" align="stretch" wrap="nowrap" gap={0}>
            <Group
              gap={0}
              wrap="nowrap"
              style={{ overflowX: 'auto', flex: 1, minWidth: 0 }}
              data-testid="active-session-preview-step-tabs"
            >
              {state.sections.map((s, idx) => {
                const isActive = idx === currentSectionIndex;
                const allotted = s.duration_minutes;
                const nameText = `${idx + 1}. ${s.section_name ?? 'Untitled section'}`;
                // Static preview: the active step shows 0:00 against its budget;
                // others show their allotment (elapsed time never ticks).
                const timeLabel =
                  allotted !== null
                    ? isActive
                      ? `${formatStaticElapsed(0)} / ${allotted}m`
                      : `${allotted} min`
                    : isActive
                      ? formatStaticElapsed(0)
                      : '—';
                return (
                  <UnstyledButton
                    key={`${s.id ?? s.section_name ?? 'section'}-${idx}`}
                    onClick={() => setCurrentSectionIndex(idx)}
                    data-testid={`active-session-preview-step-tab-${idx}`}
                    data-active={isActive ? 'true' : 'false'}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      flexShrink: 0,
                      padding: '15px 20px 9px',
                      borderBottom: `3px solid ${isActive ? TAB_ACTIVE : 'transparent'}`,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? TAB_ACTIVE : TAB_INACTIVE,
                      }}
                    >
                      {nameText}
                    </span>
                    <Text
                      fz={12}
                      fw={isActive ? 700 : 500}
                      c={TAB_INACTIVE}
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {timeLabel}
                    </Text>
                  </UnstyledButton>
                );
              })}

            </Group>

            {/* Section nav (MED-1011) — Previous + Next; no End Session in the
                read-only preview (AC4). */}
            <Group
              gap={8}
              align="center"
              wrap="nowrap"
              style={{ paddingTop: 8, paddingBottom: 8, paddingLeft: 12 }}
            >
              <Button
                variant="default"
                radius={8}
                h={38}
                w={NAV_BTN_WIDTH}
                onClick={() =>
                  setCurrentSectionIndex(idx => Math.max(0, idx - 1))
                }
                disabled={!canRetreat}
                data-testid="active-session-preview-prev-button"
                styles={{ root: { borderColor: BAND_BORDER, color: '#374151' } }}
              >
                Previous
              </Button>
              <Button
                radius={8}
                h={38}
                w={NAV_BTN_WIDTH}
                rightSection={<ArrowRight size={14} />}
                onClick={() =>
                  setCurrentSectionIndex(idx =>
                    Math.min(sectionCount - 1, idx + 1)
                  )
                }
                disabled={!canAdvance}
                data-testid="active-session-preview-next-button"
                styles={{ root: { backgroundColor: NAV_NAVY, color: '#fff' } }}
              >
                Next
              </Button>
            </Group>
          </Group>
        </Box>

        {/* §D SHOW band — full-width white band with the outlined SHOW pills.
            Coach view only; the participant view has no coach panels to toggle. */}
        {viewMode === 'coach' && (
          <Box
            style={{
              backgroundColor: BAND_BG,
              borderBottom: `1px solid ${BAND_BORDER}`,
              padding: '8px 24px 9px',
            }}
          >
            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              data-testid="active-session-preview-show-bar"
            >
              <Group align="center" gap={6} style={{ flexWrap: 'wrap' }}>
                <Text
                  fz={11}
                  fw={700}
                  c={TAB_INACTIVE}
                  tt="uppercase"
                  style={{ letterSpacing: '0.44px' }}
                >
                  Show
                </Text>
                <ShowToggle
                  active={showGuide}
                  label="Guide"
                  icon={<BookOpen size={12} />}
                  onClick={() => setShowGuide(v => !v)}
                  data-testid="active-session-preview-show-guide"
                />
                <ShowToggle
                  active={showNotes}
                  label="Notes"
                  icon={<Edit3 size={12} />}
                  onClick={() => setShowNotes(v => !v)}
                  data-testid="active-session-preview-show-notes"
                />
                {showAiInsightsToggle && (
                  <ShowToggle
                    active={showAiInsights}
                    label="AI Insights"
                    icon={<Zap size={12} />}
                    onClick={() => setShowAiInsights(v => !v)}
                    data-testid="active-session-preview-show-ai-insights"
                  />
                )}
                <ShowToggle
                  active={showParticipants}
                  label="Participants"
                  icon={<Users size={12} />}
                  badge={state.participants.length}
                  onClick={() => setShowParticipants(v => !v)}
                  data-testid="active-session-preview-show-participants"
                />
              </Group>
              <Text size="xs" c="dimmed">
                Toggle panels to validate your preferred layout.
              </Text>
            </Group>
          </Box>
        )}

        {/* Body — panel grid (coach) or the read-only participant view. */}
        <Container size="xl" py="md">
          {viewMode === 'participant' ? (
            /* Participant View — the read-only experience participants see
               (MED-805): the authored Participant Prompt + the section's
               slide(s). Section navigation (above) drives which section is
               shown, mirroring how advancing the live session updates the
               participant screen. Rendered on the participant dark surface for
               visual fidelity. The commitment-entry field is omitted — preview
               is read-only and must not write participant commitments. */
            <Box
              style={{ background: '#0E1B33', borderRadius: 8, padding: 24 }}
              data-testid="active-session-preview-participant-view"
            >
              <Container size="sm" px={0}>
                <ActiveSessionSlideCard
                  sections={participantSections}
                  currentSectionIndex={currentSectionIndex}
                  slideDeck={participantSlideDeck}
                  cohortId={cohortId}
                  sessionId={sessionId}
                  currentSlidePage={previewSlidePage}
                  lastSessionCommitment={null}
                />
                <ParticipantSlideCounter
                  visuals={participantSections[currentSectionIndex]?.visuals ?? []}
                  currentSlidePage={previewSlidePage}
                />
              </Container>
            </Box>
          ) : (
          /* 3-panel layout + optional AI Insights stub in right column. Grid
              height is pinned to the remaining viewport so a long Session Guide
              scrolls inside its column rather than stretching the whiteboard. */
          <Box
            ref={panelGridRef}
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: buildGridColumns({
                guide: showGuide,
                notes: showNotes,
                participants: showParticipants,
                aiInsights: showAiInsights,
              }),
              alignItems: 'stretch',
              height: panelGridHeight ?? undefined,
              minHeight: MIN_PANEL_GRID_HEIGHT,
            }}
            data-testid="active-session-preview-panel-grid"
          >
            {showGuide && (
              <ActiveSessionGuidePanel
                section={currentSection}
                sectionIndex={currentSectionIndex}
                leaderNotes={state.leader_notes}
              />
            )}

            <ActiveSessionWhiteboardPanel
              sections={state.sections}
              sectionIndex={currentSectionIndex}
              slideDeck={state.slide_deck}
              cohortId={cohortId}
              sessionId={sessionId}
              currentSlidePage={previewSlidePage}
              onSelectSlide={setPreviewSlidePage}
            />

            {(showNotes || showParticipants || showAiInsights) && (
              <Stack gap="md" h="100%" style={{ minHeight: 0, overflowY: 'auto' }}>
                {showNotes && (
                  <ActiveSessionNotesPanel
                    sessionId={sessionId}
                    initialHtml={state.coach_session_notes_html}
                    initialUpdatedAt={state.coach_session_notes_updated_at}
                    isReadOnly
                  />
                )}
                {showAiInsights && <ActiveSessionAiInsightsPanel />}
                {showParticipants && (
                  <ActiveSessionParticipantsPanel participants={state.participants} />
                )}
              </Stack>
            )}
          </Box>
          )}
        </Container>
    </Box>
  );
}

function buildGridColumns(opts: {
  guide: boolean;
  notes: boolean;
  participants: boolean;
  aiInsights: boolean;
}): string {
  const left = opts.guide ? '320px' : '';
  const right = opts.notes || opts.participants || opts.aiInsights ? '340px' : '';
  return [left, '1fr', right].filter(Boolean).join(' ');
}
